import React, { useState, useEffect } from 'react';
import { 
  HiOutlineUpload, 
  HiOutlineDownload, 
  HiOutlineDocumentText, 
  HiOutlineCog,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineInformationCircle,
  HiOutlineEye,
  HiOutlineTrash,
  HiOutlineRefresh
} from 'react-icons/hi';

export default function AdminImportExport() {
  const [activeTab, setActiveTab] = useState('import');
  const [importFile, setImportFile] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState('idle'); // idle, uploading, processing, success, error
  const [importResults, setImportResults] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportConfig, setExportConfig] = useState({
    includeImages: false,
    categories: 'all',
    language: 'tr'
  });
  const [exportProgress, setExportProgress] = useState({
    status: 'idle',
    message: '',
    details: []
  });
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchCategories();
    fetchStats();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/products/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Kategoriler yüklenemedi:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/products/export-stats');
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('İstatistikler yüklenemedi:', error);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImportFile(file);
      setImportStatus('idle');
      setImportResults(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;

    setImportStatus('uploading');
    setImportProgress(0);

    const formData = new FormData();
    formData.append('file', importFile);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setImportProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      const response = await fetch('/api/products/import', {
        method: 'POST',
        body: formData
      });

      clearInterval(progressInterval);
      setImportProgress(100);

      if (response.ok) {
        const result = await response.json();
        setImportResults(result);
        setImportStatus('success');
        fetchStats(); // Refresh stats after import
      } else {
        const error = await response.json();
        setImportResults({ error: error.message || 'Import başarısız' });
        setImportStatus('error');
      }
    } catch (error) {
      setImportResults({ error: 'Import sırasında hata oluştu' });
      setImportStatus('error');
    }
  };

  const handleExport = async () => {
    setExportLoading(true);
    setExportProgress({
      status: 'starting',
      message: 'Export başlatılıyor...',
      details: []
    });
    
    try {
      const params = new URLSearchParams({
        kategori: exportConfig.categories === 'all' ? '' : exportConfig.categories,
        includeImages: exportConfig.includeImages.toString(),
        language: exportConfig.language,
        format: 'detailed'
      });

      console.log('Export parametreleri:', params.toString());
      
      setExportProgress(prev => ({
        ...prev,
        status: 'fetching',
        message: 'Ürün verileri alınıyor...',
        details: [...prev.details, '📊 Veritabanından ürünler sorgulanıyor...']
      }));

      // Mevcut API endpoint'ini kullan
      const response = await fetch(`/api/products/export-excel?${params}`);
      
      console.log('Export response status:', response.status);
      console.log('Export response headers:', response.headers);
      
      if (response.ok) {
        setExportProgress(prev => ({
          ...prev,
          status: 'processing',
          message: 'Dosya oluşturuluyor...',
          details: [...prev.details, '✅ Veriler başarıyla alındı', '📄 Excel dosyası oluşturuluyor...']
        }));
        
        const blob = await response.blob();
        console.log('Export blob size:', blob.size);
        
        if (blob.size === 0) {
          throw new Error('Export edilen dosya boş');
        }
        
        setExportProgress(prev => ({
          ...prev,
          status: 'downloading',
          message: 'Dosya indiriliyor...',
          details: [...prev.details, '✅ Excel dosyası oluşturuldu', '📥 Dosya indiriliyor...']
        }));
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = exportConfig.includeImages 
          ? `urunler_export_${new Date().toISOString().split('T')[0]}.zip`
          : `urunler_export_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        setExportProgress({
          status: 'success',
          message: 'Export başarıyla tamamlandı!',
          details: [...exportProgress.details, '✅ Dosya başarıyla indirildi']
        });
        
        console.log('Export başarılı');
      } else {
        const errorText = await response.text();
        console.error('Export response error:', errorText);
        throw new Error(`Export başarısız: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Export hatası:', error);
      setExportProgress({
        status: 'error',
        message: 'Export sırasında hata oluştu',
        details: [...exportProgress.details, `❌ Hata: ${error.message}`]
      });
      alert('Export sırasında hata oluştu: ' + error.message);
    } finally {
      setExportLoading(false);
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setImportProgress(0);
    setImportStatus('idle');
    setImportResults(null);
  };

  const getStatusIcon = () => {
    switch (importStatus) {
      case 'success':
        return <HiOutlineCheckCircle className="w-6 h-6 text-green-500" />;
      case 'error':
        return <HiOutlineXCircle className="w-6 h-6 text-red-500" />;
      case 'uploading':
        return <HiOutlineRefresh className="w-6 h-6 text-blue-500 animate-spin" />;
      default:
        return <HiOutlineInformationCircle className="w-6 h-6 text-gray-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white">Ürün Import/Export</h1>
                <p className="text-blue-100 mt-2 text-lg">Ürün verilerini içe aktarın ve dışa aktarın</p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-right text-blue-100">
                  <div className="text-sm opacity-75">Toplam Ürün</div>
                  <div className="text-2xl font-bold">{stats?.totalProducts || 0}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 bg-gray-50">
            <nav className="flex space-x-8 px-8">
              <button
                onClick={() => setActiveTab('import')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-all duration-200 ${
                  activeTab === 'import'
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center">
                  <HiOutlineUpload className="mr-2" />
                  Import
                </span>
              </button>
              <button
                onClick={() => setActiveTab('export')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-all duration-200 ${
                  activeTab === 'export'
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="flex items-center">
                  <HiOutlineDownload className="mr-2" />
                  Export
                </span>
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="p-8">
            {activeTab === 'import' && (
              <div className="space-y-8">
                {/* Import Section */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center mb-6">
                    <HiOutlineUpload className="w-6 h-6 text-blue-600 mr-3" />
                    <h2 className="text-xl font-semibold text-gray-900">Ürün Import</h2>
                  </div>

                  {/* File Upload */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors duration-200">
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <HiOutlineUpload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <div className="text-lg font-medium text-gray-900 mb-2">
                        {importFile ? importFile.name : 'Dosya seçin veya sürükleyin'}
                      </div>
                      <div className="text-sm text-gray-500">
                        Excel (.xlsx, .xls) veya CSV dosyaları desteklenir
                      </div>
                    </label>
                  </div>

                  {/* Progress Bar */}
                  {importStatus === 'uploading' && (
                    <div className="mt-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Yükleniyor...</span>
                        <span className="text-sm text-gray-500">{importProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${importProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Import Button */}
                  {importFile && importStatus === 'idle' && (
                    <div className="mt-6">
                      <button
                        onClick={handleImport}
                        className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center"
                      >
                        <HiOutlineUpload className="mr-2" />
                        Import Başlat
                      </button>
                    </div>
                  )}

                  {/* Results */}
                  {importResults && (
                    <div className={`mt-6 p-4 rounded-lg ${
                      importStatus === 'success' 
                        ? 'bg-green-50 border border-green-200' 
                        : 'bg-red-50 border border-red-200'
                    }`}>
                      <div className="flex items-center">
                        {getStatusIcon()}
                        <div className="ml-3">
                          <h3 className={`text-sm font-medium ${
                            importStatus === 'success' ? 'text-green-800' : 'text-red-800'
                          }`}>
                            {importStatus === 'success' ? 'Import Başarılı' : 'Import Hatası'}
                          </h3>
                          <div className={`mt-1 text-sm ${
                            importStatus === 'success' ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {importStatus === 'success' ? (
                              <div>
                                <p>Başarıyla import edilen ürünler: {importResults.successCount || 0}</p>
                                {importResults.errors && importResults.errors.length > 0 && (
                                  <p>Hatalı satırlar: {importResults.errors.length}</p>
                                )}
                              </div>
                            ) : (
                              <p>{importResults.error}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      {importStatus === 'success' && (
                        <button
                          onClick={resetImport}
                          className="mt-4 bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 transition-colors duration-200"
                        >
                          Yeni Import
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Import Instructions */}
                <div className="bg-blue-50 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <HiOutlineInformationCircle className="w-6 h-6 text-blue-600 mr-3" />
                    <h3 className="text-lg font-semibold text-blue-900">Import Talimatları</h3>
                  </div>
                  <div className="text-blue-800 space-y-2">
                    <p>• Excel dosyasında aşağıdaki sütunlar bulunmalıdır:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Ürün Adı (zorunlu)</li>
                      <li>Kategori (zorunlu)</li>
                      <li>Fiyat (zorunlu)</li>
                      <li>Stok Miktarı (opsiyonel)</li>
                      <li>Açıklama (opsiyonel)</li>
                      <li>Resim URL (opsiyonel)</li>
                    </ul>
                    <p className="mt-4">• İlk satır başlık satırı olarak kabul edilir</p>
                    <p>• Maksimum dosya boyutu: 10MB</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'export' && (
              <div className="space-y-8">
                {/* Export Configuration */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center mb-6">
                    <HiOutlineDownload className="w-6 h-6 text-blue-600 mr-3" />
                    <h2 className="text-xl font-semibold text-gray-900">Ürün Export</h2>
                  </div>
                  
                  <div className="mb-6 bg-blue-50 rounded-lg p-4">
                    <div className="flex items-start">
                      <HiOutlineInformationCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Export Seçenekleri:</p>
                        <ul className="list-disc list-inside space-y-1">
                          <li><strong>Excel Dosyası:</strong> Sadece ürün verilerini içerir</li>
                          <li><strong>ZIP Dosyası:</strong> Excel dosyası + resimler/ klasöründe ürün resimleri</li>
                          <li>Resimler Excel'e gömülmez, ayrı klasörde tutulur</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Language Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Dil
                      </label>
                      <select
                        value={exportConfig.language}
                        onChange={(e) => setExportConfig({...exportConfig, language: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="tr">Türkçe</option>
                        <option value="en">İngilizce</option>
                      </select>
                    </div>

                    {/* Category Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Kategori Filtresi
                      </label>
                      <select
                        value={exportConfig.categories}
                        onChange={(e) => setExportConfig({...exportConfig, categories: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="all">Tüm Kategoriler</option>
                        {categories.map((category) => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>

                    {/* Include Images */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="includeImages"
                        checked={exportConfig.includeImages}
                        onChange={(e) => setExportConfig({...exportConfig, includeImages: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="includeImages" className="ml-2 block text-sm text-gray-700">
                        Resimleri Dahil Et (ZIP dosyasında resimler/ klasöründe)
                      </label>
                    </div>
                  </div>

                  {/* Export Button */}
                  <div className="mt-6">
                    <button
                      onClick={handleExport}
                      disabled={exportLoading}
                      className="w-full bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center"
                    >
                      {exportLoading ? (
                        <>
                          <HiOutlineRefresh className="mr-2 animate-spin" />
                          Export İşleniyor...
                        </>
                      ) : (
                        <>
                          <HiOutlineDownload className="mr-2" />
                          Export Başlat
                        </>
                      )}
                    </button>
                  </div>

                  {/* Export Progress */}
                  {exportProgress.status !== 'idle' && (
                    <div className="mt-6">
                      <div className={`p-4 rounded-lg border ${
                        exportProgress.status === 'success' ? 'bg-green-50 border-green-200' :
                        exportProgress.status === 'error' ? 'bg-red-50 border-red-200' :
                        'bg-blue-50 border-blue-200'
                      }`}>
                        <div className="flex items-center mb-3">
                          {exportProgress.status === 'success' ? (
                            <HiOutlineCheckCircle className="w-5 h-5 text-green-600 mr-2" />
                          ) : exportProgress.status === 'error' ? (
                            <HiOutlineXCircle className="w-5 h-5 text-red-600 mr-2" />
                          ) : (
                            <HiOutlineRefresh className="w-5 h-5 text-blue-600 mr-2 animate-spin" />
                          )}
                          <span className={`font-medium ${
                            exportProgress.status === 'success' ? 'text-green-800' :
                            exportProgress.status === 'error' ? 'text-red-800' :
                            'text-blue-800'
                          }`}>
                            {exportProgress.message}
                          </span>
                        </div>
                        
                        {exportProgress.details.length > 0 && (
                          <div className="space-y-1">
                            {exportProgress.details.map((detail, index) => (
                              <div key={index} className="text-sm text-gray-700 flex items-center">
                                <span className="mr-2">•</span>
                                {detail}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Statistics */}
                {stats && (
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 border border-green-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 Sistem İstatistikleri</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-2xl font-bold text-blue-600">{stats.totalProducts}</div>
                        <div className="text-sm text-gray-600">Toplam Ürün</div>
                      </div>
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-2xl font-bold text-green-600">{stats.stockStatus?.inStock || 0}</div>
                        <div className="text-sm text-gray-600">Stokta</div>
                      </div>
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-2xl font-bold text-red-600">{stats.stockStatus?.outOfStock || 0}</div>
                        <div className="text-sm text-gray-600">Stokta Yok</div>
                      </div>
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-2xl font-bold text-orange-600">{stats.stockStatus?.lowStock || 0}</div>
                        <div className="text-sm text-gray-600">Kritik Stok</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 